import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Divider,
  useTheme as useMuiTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import {
  Inbox as InboxIcon,
  Send as SendIcon,
  Block as SpamIcon,
  Cancel as NoReplyIcon,
  Folder as ArchiveFolderIcon,
  Label as LabelIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  HourglassTop as HourglassTopIcon,
} from "@mui/icons-material";
// TagIcon used for sub-label display (# sub label)
import TagIcon from "@mui/icons-material/Tag";
import { useTheme } from "../contexts/ThemeContext";
import {
  getEmailMetrics,
  EmailMetric,
  LabelHierarchyItem,
  LabelHierarchySubLabel,
} from "../services/emailService";
import { STORAGE_KEYS } from "../helpers/constants";
import { capitalizeFirstLetter } from "../helpers/stringUtils";

// Constants
const STANDARD_CATEGORIES = ['inbox', 'sent', 'spam', 'no_reply', 'archived'];
/** Categories that accept drag-and-drop move (whole thread) */
const DROP_TARGET_CATEGORIES = ['inbox', 'spam', 'no_reply'];
const THREAD_MOVE_DRAG_TYPE = 'application/x-shay-thread-move';

// Types
export interface EmailCategory {
  label: string;
  key: string;
  icon: React.ComponentType<any>;
  count: number;
}

// Classification label with optional sub-labels for "Label / # sub label" UI
export interface ClassificationLabel extends EmailCategory {
  subLabels: { key: string; name: string }[];
}

export interface EmailCategoriesResult {
  categories: EmailCategory[];
  labels: ClassificationLabel[];
}

// Props interface
export interface EmailClassificationPanelProps {
  channelId: string | null;
  channelType: string;
  selectedEmailCategory: string;
  onCategoryChange: (category: string) => void;
  onCategorySelect?: (category: string, label: string) => void; // Optional callback with both key and label
  /** Called when metrics are loaded so parent can show total count in header */
  onMetricsLoaded?: (metrics: EmailMetric[]) => void;
  /** When provided, use these metrics for display (e.g. after parent marks a thread as read) so counts update without refetch */
  externalMetrics?: EmailMetric[] | null;
  /** When provided, use this label hierarchy for classification section (overrides internal from API) */
  externalLabelHierarchy?: LabelHierarchyItem[] | null;
  zIndex?: number;
  /** Drag-and-drop: when a thread is dropped on a category (inbox, spam, no_reply), call with target key and drag payload */
  onThreadDropOnCategory?: (
    targetCategory: string,
    payload: { threadId: string; channelId: string; external_app_id: string | null }
  ) => void;
}

const EmailClassificationPanel: React.FC<EmailClassificationPanelProps> = ({
  channelId,
  channelType,
  selectedEmailCategory,
  onCategoryChange,
  onCategorySelect,
  onMetricsLoaded,
  externalMetrics,
  externalLabelHierarchy,
  zIndex = 1,
  onThreadDropOnCategory,
}) => {
  const { isDarkMode } = useTheme();
  const muiTheme = useMuiTheme();

  // State for Email Classifier panel shrink functionality
  const [isEmailClassifierShrunk, setIsEmailClassifierShrunk] = useState<boolean>(false);

  // State for email metrics (standard categories only) and label hierarchy (classification labels)
  const [emailMetrics, setEmailMetrics] = useState<EmailMetric[]>([]);
  const [labelHierarchy, setLabelHierarchy] = useState<LabelHierarchyItem[]>([]);
  const [metricsLoading, setMetricsLoading] = useState<boolean>(false);
  const [metricsError, setMetricsError] = useState<string | null>(null);
  const [trainingInProgress, setTrainingInProgress] = useState<boolean>(false);
  /** While dragging a thread onto the sidebar, which drop target row is hovered (preview before drop) */
  const [dragOverCategoryKey, setDragOverCategoryKey] = useState<string | null>(null);

  // Clear drag-over highlight when the drag ends anywhere (cancel, drop outside, or successful drop)
  useEffect(() => {
    if (!onThreadDropOnCategory) return;
    const onDragEnd = () => setDragOverCategoryKey(null);
    window.addEventListener('dragend', onDragEnd);
    return () => window.removeEventListener('dragend', onDragEnd);
  }, [onThreadDropOnCategory]);

  // Helper: only clear hover when pointer truly leaves a drop row (not when moving to another drop target)
  const handleCategoryDragLeave = (e: React.DragEvent<HTMLElement>) => {
    const rel = e.relatedTarget as HTMLElement | null;
    if (rel?.closest?.('[data-email-drop-target]')) return;
    setDragOverCategoryKey(null);
  };

  // Helper function to get icon based on metric key
  const getIconForMetricKey = (key: string) => {
    const iconMap: { [key: string]: any } = {
      inbox: InboxIcon,
      sent: SendIcon,
      spam: SpamIcon,
      no_reply: NoReplyIcon,
      archived: ArchiveFolderIcon,
    };
    return iconMap[key] || InboxIcon; // Default to InboxIcon if not found
  };

  // Check if a metric key is a standard category (not a label)
  const isStandardCategory = (key: string): boolean => {
    return STANDARD_CATEGORIES.includes(key);
  };

  // Build categories from metrics (standard only) and labels from label_hierarchy (new API format)
  // Prefer externalMetrics / externalLabelHierarchy when provided for display updates
  const getEmailCategories = (): EmailCategoriesResult => {
    const internalMetrics = Array.isArray(emailMetrics) ? emailMetrics : [];
    const validMetrics =
      externalMetrics && externalMetrics.length > 0 ? externalMetrics : internalMetrics;
    const internalHierarchy = Array.isArray(labelHierarchy) ? labelHierarchy : [];
    const validHierarchy =
      externalLabelHierarchy && externalLabelHierarchy.length > 0
        ? externalLabelHierarchy
        : internalHierarchy;

    // Default categories when no metrics yet
    if (validMetrics.length === 0 && !metricsLoading) {
      const defaultCategories: EmailCategory[] = [
        { label: "Inbox", key: "inbox", icon: InboxIcon, count: 0 },
        { label: "Sent", key: "sent", icon: SendIcon, count: 0 },
        { label: "Spam", key: "spam", icon: SpamIcon, count: 0 },
        { label: "No Reply", key: "no_reply", icon: NoReplyIcon, count: 0 },
      ];
      return { categories: [...defaultCategories], labels: [] };
    }

    // Categories from metrics (inbox, sent, no_reply, spam only in new API)
    const categories: EmailCategory[] = validMetrics
      .filter((m) => isStandardCategory(m.key))
      .map((metric) => ({
        label: capitalizeFirstLetter(metric.name),
        key: metric.key,
        icon: getIconForMetricKey(metric.key),
        count: metric.unread_count ?? 0,
      }));

    // Labels from label_hierarchy; use flattened validMetrics for count when provided (e.g. after mark read)
    const labels: ClassificationLabel[] = validHierarchy.map((item) => ({
      label: capitalizeFirstLetter(item.name),
      key: item.key,
      icon: LabelIcon,
      count:
        validMetrics.find((m) => m.key === item.key)?.unread_count ??
        item.unread_count ??
        0,
      subLabels: (item.sub_labels ?? []).map((s: LabelHierarchySubLabel) => ({
        key: s.key,
        name: capitalizeFirstLetter(s.name),
      })),
    }));

    return { categories, labels };
  };

  // Fetch email metrics from API
  const fetchEmailMetrics = async () => {
    if (!channelId) {
      return;
    }

    try {
      setMetricsLoading(true);
      setMetricsError(null);

      const token = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) || "";
      const response = await getEmailMetrics(channelId, token);
      if (response.success) {
        const data = response.data;
        setEmailMetrics(data.metrics ?? []);
        setLabelHierarchy(data.label_hierarchy ?? []);
        setTrainingInProgress(data.training_in_progress || false);
        // Flatten metrics + label_hierarchy so parent can show count for any selected key (inbox, label_xxx, sublabel_xxx)
        const flat = [
          ...(data.metrics ?? []),
          ...(data.label_hierarchy ?? []).flatMap((l) => [
            { key: l.key, count: l.count, unread_count: l.unread_count },
            ...(l.sub_labels ?? []).map((s) => ({
              key: s.key,
              count: s.count,
              unread_count: s.unread_count,
            })),
          ]),
        ] as EmailMetric[];
        onMetricsLoaded?.(flat);
      } else {
        throw new Error("Failed to fetch email metrics");
      }
    } catch (err) {
      let errorMessage = "Failed to fetch email metrics";

      if (err instanceof Error) {
        errorMessage = err.message;
      }

      setMetricsError(errorMessage);
    } finally {
      setMetricsLoading(false);
    }
  };

  // Fetch email metrics when channelId changes
  useEffect(() => {
    if (channelId && channelType === 'email') {
      fetchEmailMetrics();
    }
  }, [channelId, channelType]);

  // When retrain is triggered from channel settings, show training in progress on threads page without refresh
  useEffect(() => {
    if (typeof window === 'undefined' || !channelId || channelType !== 'email') return;
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<{ channelId: string }>).detail;
      if (detail?.channelId === channelId) setTrainingInProgress(true);
    };
    window.addEventListener('channel-classification-training-started', handler);
    return () => window.removeEventListener('channel-classification-training-started', handler);
  }, [channelId, channelType]);

  // Poll metrics while training in progress so we clear "Processing your inbox" when backend finishes
  useEffect(() => {
    if (!channelId || channelType !== 'email' || !trainingInProgress) return;
    const interval = setInterval(() => fetchEmailMetrics(), 15000);
    return () => clearInterval(interval);
  }, [channelId, channelType, trainingInProgress]);

  // Don't render if not an email channel
  if (channelType !== 'email') {
    return null;
  }

  const { categories, labels } = getEmailCategories();

  return (
    <Box
      data-testid="email-classifier-panel"
      sx={{
        flex: isEmailClassifierShrunk ? "0 0 50px" : "0 0 200px",
        minWidth: isEmailClassifierShrunk ? "50px" : "200px",
        maxWidth: isEmailClassifierShrunk ? "50px" : "250px",
        overflow: "hidden",
        position: "relative",
        zIndex: zIndex,
        backgroundColor: muiTheme.palette.background.paper,
        borderRight: `1px solid #f0f0f0`,
        transition: "all 0.3s ease-in-out",
      }}
    >
      {/* Email Classifier Section */}
      <Box 
        data-testid="email-classifier-content" 
        sx={{ 
          p: 1,
          height: "100%",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {/* Header with Shrink/Expand Button - Compact Design */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 0.5,
            minHeight: "32px",
            flexShrink: 0,
          }}
        >
          {!isEmailClassifierShrunk && (
            <Typography
              data-testid="email-classifier-title"
              variant="body2"
              sx={{
                color: "text.secondary",
                fontSize: "0.875rem",
                fontWeight: 500,
                textTransform: "uppercase",
                letterSpacing: "0.5px",
              }}
            >
              &nbsp;Categories
            </Typography>
          )}
          <IconButton
            data-testid="email-classifier-toggle-button"
            onClick={() =>
              setIsEmailClassifierShrunk(!isEmailClassifierShrunk)
            }
            size="small"
            sx={{
              color: "text.secondary",
              padding: "6px 4px",
              minWidth: "auto",
              width: "32px",
              height: "32px",
              "&:hover": {
                backgroundColor: muiTheme.palette.action.hover,
              },
            }}
          >
            {isEmailClassifierShrunk ? (
              <ChevronRightIcon sx={{ fontSize: "20px" }} />
            ) : (
              <ChevronLeftIcon sx={{ fontSize: "20px" }} />
            )}
          </IconButton>
        </Box>

        {!isEmailClassifierShrunk && (
          <Box
            sx={{
              flex: 1,
              overflowY: "auto",
              overflowX: "hidden",
              pr: 0.5,
              "&::-webkit-scrollbar": {
                width: "6px",
              },
              "&::-webkit-scrollbar-track": {
                background: "transparent",
              },
              "&::-webkit-scrollbar-thumb": {
                background: isDarkMode 
                  ? "rgba(255,255,255,0.2)" 
                  : "rgba(0,0,0,0.2)",
                borderRadius: "3px",
                "&:hover": {
                  background: isDarkMode 
                    ? "rgba(255,255,255,0.3)" 
                    : "rgba(0,0,0,0.3)",
                },
              },
            }}
          >
            <List data-testid="email-classifier-list-expanded" sx={{ p: 0 }}>
              <>
                {/* Render Categories */}
                {categories.map((category) => {
                  const isDropTarget = onThreadDropOnCategory && DROP_TARGET_CATEGORIES.includes(category.key);
                  const isDragOverTarget = isDropTarget && dragOverCategoryKey === category.key;
                  return (
                  <ListItemButton
                    key={category.key}
                    data-testid={`email-category-${category.key}`}
                    data-email-drop-target={isDropTarget ? category.key : undefined}
                    selected={category.key === selectedEmailCategory}
                    onClick={() => {
                      onCategoryChange(category.key);
                      onCategorySelect?.(category.key, category.label);
                    }}
                    onDragEnter={isDropTarget ? (e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setDragOverCategoryKey(category.key);
                    } : undefined}
                    onDragOver={isDropTarget ? (e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      e.dataTransfer.dropEffect = 'move';
                      setDragOverCategoryKey(category.key);
                    } : undefined}
                    onDragLeave={isDropTarget ? handleCategoryDragLeave : undefined}
                    onDrop={isDropTarget ? (e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setDragOverCategoryKey(null);
                      const raw = e.dataTransfer.getData(THREAD_MOVE_DRAG_TYPE);
                      if (!raw) return;
                      try {
                        const payload = JSON.parse(raw) as { threadId: string; channelId: string; external_app_id: string | null };
                        if (payload.threadId && payload.channelId) {
                          onThreadDropOnCategory(category.key, payload);
                        }
                      } catch (_) { /* ignore */ }
                    } : undefined}
                    sx={{
                      borderRadius: 1,
                      mb: 0.5,
                      py: 0,
                      minHeight: 44,
                      height: 44,
                      boxSizing: "border-box",
                      px: "8px",
                      display: "flex",
                      alignItems: "center",
                      width: "100%",
                      ...(isDropTarget && {
                        "&:hover": {
                          backgroundColor: muiTheme.palette.action.hover,
                        },
                      }),
                      "&.Mui-selected": {
                        backgroundColor: alpha(
                          muiTheme.palette.primary.main,
                          isDarkMode ? 0.32 : 0.22
                        ),
                        boxShadow: `inset 3px 0 0 ${muiTheme.palette.primary.main}`,
                        "&:hover": {
                          backgroundColor: alpha(
                            muiTheme.palette.primary.main,
                            isDarkMode ? 0.42 : 0.3
                          ),
                        },
                        // Keep label regular weight (MUI selected often uses medium)
                        "& .MuiListItemText-primary": { fontWeight: 400 },
                      },
                      // Drag-over preview: stronger tint + visible edge so the drop target is easy to spot
                      ...(isDragOverTarget && {
                        "&, &.Mui-selected": {
                          backgroundColor: alpha(
                            muiTheme.palette.primary.main,
                            isDarkMode ? 0.38 : 0.22
                          ),
                          boxShadow: `inset 0 0 0 1px ${alpha(muiTheme.palette.primary.main, isDarkMode ? 0.58 : 0.45)}`,
                        },
                      }),
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: 32,
                        color: muiTheme.palette.text.secondary,
                      }}
                    >
                      <category.icon sx={{ fontSize: "1.1rem" }} />
                    </ListItemIcon>
                    <Tooltip title={category.label} placement="right" arrow>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <ListItemText
                          primary={category.label}
                          sx={{
                            "& .MuiListItemText-primary": {
                              fontSize: "0.875rem",
                              fontWeight: 400,
                              color: muiTheme.palette.text.primary,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            },
                          }}
                        />
                      </Box>
                    </Tooltip>
                    {category.count > 0 && (
                      <Box
                        sx={{
                          backgroundColor: "#dd4b39", // Consistent gray background for all count badges
                          color: "white",
                          borderRadius: 1,
                          minWidth: category.count >= 10 ? "24px" : "20px",
                          height: "20px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: "0.7rem",
                          fontWeight: "bold",
                          ml: 1,
                          px: 0.5,
                          flexShrink: 0,
                          boxShadow: "0 2px 4px rgba(0,0,0,0.15)",
                          border: "1px solid rgba(255,255,255,0.2)",
                        }}
                      >
                        {category.count}
                      </Box>
                    )}
                  </ListItemButton>
                  );
                })}
                
                {/* Separator between categories and classification labels */}
                {(labels.length > 0 || trainingInProgress) && (
                  <Divider sx={{ my: 1, mx: 1 }} />
                )}

                {/* Classification section header - hide when training so only loading message shows */}
                {labels.length > 0 && !trainingInProgress && (
                  <Typography
                    variant="body2"
                    sx={{
                      color: "text.secondary",
                      fontSize: "0.875rem",
                      fontWeight: 500,
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                      px: 1,
                      py: 0.5,
                      mt: 0.5,
                    }}
                  >
                    Classification
                  </Typography>
                )}

                {/* Render Labels with sub-labels - hide when training in progress */}
                {!trainingInProgress && labels.map((label) => {
                  const isLabelSelected = selectedEmailCategory === label.key;
                  return (
                    <Box key={label.key} sx={{ mb: 0.25 }}>
                      {/* Parent label row: label icon + label name */}
                      <ListItemButton
                        data-testid={`email-label-${label.key}`}
                        selected={isLabelSelected}
                        onClick={() => {
                          onCategoryChange(label.key);
                          onCategorySelect?.(label.key, label.label);
                        }}
                        sx={{
                          borderRadius: 1,
                          py: 0.5,
                          px: "8px",
                          "&.Mui-selected": {
                            backgroundColor: alpha(
                              muiTheme.palette.primary.main,
                              isDarkMode ? 0.32 : 0.22
                            ),
                            boxShadow: `inset 3px 0 0 ${muiTheme.palette.primary.main}`,
                            "&:hover": {
                              backgroundColor: alpha(
                                muiTheme.palette.primary.main,
                                isDarkMode ? 0.42 : 0.3
                              ),
                            },
                            "& .MuiListItemText-primary": { fontWeight: 400 },
                          },
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: "22px", color: muiTheme.palette.text.secondary }}>
                          <LabelIcon sx={{ fontSize: "1.1rem" }} />
                        </ListItemIcon>
                        <Tooltip title={label.label} placement="right" arrow>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <ListItemText
                              primary={label.label}
                              sx={{
                                "& .MuiListItemText-primary": {
                                  fontSize: "0.875rem",
                                  fontWeight: 400,
                                  color: muiTheme.palette.text.primary,
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                },
                              }}
                            />
                          </Box>
                        </Tooltip>
                        {label.count > 0 && (
                          <Box
                            sx={{
                              backgroundColor: "#dd4b39",
                              color: "white",
                              borderRadius: 1,
                              minWidth: label.count >= 10 ? "24px" : "20px",
                              height: "20px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontSize: "0.7rem",
                              fontWeight: "bold",
                              ml: 1,
                              px: 0.5,
                              flexShrink: 0,
                              boxShadow: "0 2px 4px rgba(0,0,0,0.15)",
                              border: "1px solid rgba(255,255,255,0.2)",
                            }}
                          >
                            {label.count}
                          </Box>
                        )}
                      </ListItemButton>
                      {/* Sub-labels: indented with # icon */}
                      {label.subLabels.map((sub) => {
                        const isSubSelected = selectedEmailCategory === sub.key;
                        return (
                          <ListItemButton
                            key={sub.key}
                            data-testid={`email-sublabel-${sub.key}`}
                            selected={isSubSelected}
                            onClick={() => {
                              onCategoryChange(sub.key);
                              onCategorySelect?.(sub.key, sub.name);
                            }}
                            sx={{
                              borderRadius: 1,
                              py: 0.25,
                              pl: 3,
                              pr: "8px",
                              "&.Mui-selected": {
                                backgroundColor: alpha(
                                  muiTheme.palette.primary.main,
                                  isDarkMode ? 0.32 : 0.22
                                ),
                                boxShadow: `inset 3px 0 0 ${muiTheme.palette.primary.main}`,
                                "&:hover": {
                                  backgroundColor: alpha(
                                    muiTheme.palette.primary.main,
                                    isDarkMode ? 0.42 : 0.3
                                  ),
                                },
                                "& .MuiListItemText-primary": { fontWeight: 400 },
                              },
                            }}
                          >
                            <ListItemIcon sx={{ minWidth: "20px", color: muiTheme.palette.text.secondary }}>
                              <TagIcon sx={{ fontSize: "0.95rem" }} />
                            </ListItemIcon>
                            <Tooltip title={sub.name} placement="right" arrow>
                              <ListItemText
                                primary={sub.name}
                                sx={{
                                  "& .MuiListItemText-primary": {
                                    fontSize: "0.8125rem",
                                    fontWeight: 400,
                                    color: muiTheme.palette.text.primary,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                  },
                                }}
                              />
                            </Tooltip>
                          </ListItemButton>
                        );
                      })}
                    </Box>
                  );
                })}

                { trainingInProgress && (
                  <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, textAlign: 'center' }}>
                    {/* Single hourglass: rotation flips it so top/bottom swap at 180° (no stacking) */}
                    <Box
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        '@keyframes hourglassFlip': {
                          '0%': { transform: 'rotate(0deg)' },
                          '50%': { transform: 'rotate(180deg)' },
                          '100%': { transform: 'rotate(360deg)' },
                        },
                        '@keyframes hourglassPulse': {
                          '0%, 100%': { opacity: 1 },
                          '50%': { opacity: 0.6 },
                        },
                        animation: 'hourglassFlip 5s ease-in-out infinite, hourglassPulse 1.5s ease-in-out infinite',
                      }}
                    >
                      <HourglassTopIcon sx={{ fontSize: 24 }} color="action" />
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      Processing your inbox, Labels will be displayed automatically once training is complete.
                    </Typography>
                  </Box>
                )}
              </>
            </List>
          </Box>
        )}

        {/* Shrunk view - show only icons */}
        {isEmailClassifierShrunk && (
          <Box
            sx={{
              flex: 1,
              overflowY: "auto",
              overflowX: "hidden",
              pr: 0.5,
              "&::-webkit-scrollbar": {
                width: "6px",
              },
              "&::-webkit-scrollbar-track": {
                background: "transparent",
              },
              "&::-webkit-scrollbar-thumb": {
                background: isDarkMode 
                  ? "rgba(255,255,255,0.2)" 
                  : "rgba(0,0,0,0.2)",
                borderRadius: "3px",
                "&:hover": {
                  background: isDarkMode 
                    ? "rgba(255,255,255,0.3)" 
                    : "rgba(0,0,0,0.3)",
                },
              },
            }}
          >
            <List data-testid="email-classifier-list-shrunk" sx={{ p: 0 }}>
              <>
                {/* Render Category Icons */}
                {categories.map((category) => {
                  const isDropTarget = onThreadDropOnCategory && DROP_TARGET_CATEGORIES.includes(category.key);
                  const isDragOverTarget = isDropTarget && dragOverCategoryKey === category.key;
                  return (
                  <Tooltip
                    key={category.label}
                    title={category.label}
                    placement="right"
                  >
                    <IconButton
                      data-testid={`email-category-icon-${category.key}`}
                      data-email-drop-target={isDropTarget ? category.key : undefined}
                      onClick={() => {
                        onCategoryChange(category.key);
                        onCategorySelect?.(category.key, category.label);
                      }}
                      onDragEnter={isDropTarget ? (e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setDragOverCategoryKey(category.key);
                      } : undefined}
                      onDragOver={isDropTarget ? (e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        e.dataTransfer.dropEffect = 'move';
                        setDragOverCategoryKey(category.key);
                      } : undefined}
                      onDragLeave={isDropTarget ? handleCategoryDragLeave : undefined}
                      onDrop={isDropTarget ? (e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setDragOverCategoryKey(null);
                        const raw = e.dataTransfer.getData(THREAD_MOVE_DRAG_TYPE);
                        if (!raw) return;
                        try {
                          const payload = JSON.parse(raw) as { threadId: string; channelId: string; external_app_id: string | null };
                          if (payload.threadId && payload.channelId) {
                            onThreadDropOnCategory(category.key, payload);
                          }
                        } catch (_) { /* ignore */ }
                      } : undefined}
                      sx={{
                        width: "100%",
                        height: "40px",
                        mb: 0.5,
                        borderRadius: 1,
                        backgroundColor: isDragOverTarget
                          ? alpha(muiTheme.palette.primary.main, isDarkMode ? 0.38 : 0.22)
                          : category.key === selectedEmailCategory
                            ? alpha(
                                muiTheme.palette.primary.main,
                                isDarkMode ? 0.32 : 0.22
                              )
                            : "transparent",
                        color: "text.secondary",
                        boxShadow: isDragOverTarget
                          ? `inset 0 0 0 1px ${alpha(muiTheme.palette.primary.main, isDarkMode ? 0.58 : 0.45)}`
                          : category.key === selectedEmailCategory
                            ? `inset 3px 0 0 ${muiTheme.palette.primary.main}`
                            : "none",
                        "&:hover": {
                          backgroundColor: isDragOverTarget
                            ? alpha(muiTheme.palette.primary.main, isDarkMode ? 0.44 : 0.28)
                            : category.key === selectedEmailCategory
                              ? alpha(
                                  muiTheme.palette.primary.main,
                                  isDarkMode ? 0.42 : 0.3
                                )
                              : muiTheme.palette.action.hover,
                        },
                        position: "relative",
                      }}
                    >
                      <category.icon sx={{ fontSize: "1.2rem" }} />
                      {category.count > 0 && (
                        <Box
                          sx={{
                            position: "absolute",
                            top: 2,
                            right: 1,
                            backgroundColor: "#dd4b39", // Consistent gray background for all count badges
                            color: "white",
                            borderRadius: "20%",
                            minWidth: "16px",
                            height: "16px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: "0.65rem",
                            fontWeight: "bold",
                            boxShadow: "0 2px 4px rgba(0,0,0,0.15)",
                            border: "1px solid rgba(255,255,255,0.2)",
                          }}
                        >
                          {category.count}
                        </Box>
                      )}
                    </IconButton>
                  </Tooltip>
                  );
                })}
                
                {/* Separator between categories and labels */}
                {(labels.length > 0 || trainingInProgress) && (
                  <Divider sx={{ my: 1, mx: 1 }} />
                )}
                
                {/* Render Label Icons (shrunk) - hide when training in progress */}
                {!trainingInProgress && labels.map((label) => {
                  const isLabelSelected = selectedEmailCategory === label.key;
                  return (
                    <Tooltip
                      key={label.key}
                      title={label.label}
                      placement="right"
                    >
                      <IconButton
                        data-testid={`email-label-icon-${label.key}`}
                        onClick={() => {
                          onCategoryChange(label.key);
                          onCategorySelect?.(label.key, label.label);
                        }}
                        sx={{
                          width: "100%",
                          height: "40px",
                          mb: 0.5,
                          borderRadius: 1,
                          backgroundColor: isLabelSelected
                            ? alpha(
                                muiTheme.palette.primary.main,
                                isDarkMode ? 0.32 : 0.22
                              )
                            : "transparent",
                          color: "text.secondary",
                          boxShadow: isLabelSelected
                            ? `inset 3px 0 0 ${muiTheme.palette.primary.main}`
                            : "none",
                          "&:hover": {
                            backgroundColor: isLabelSelected
                              ? alpha(
                                  muiTheme.palette.primary.main,
                                  isDarkMode ? 0.42 : 0.3
                                )
                              : muiTheme.palette.action.hover,
                          },
                          position: "relative",
                        }}
                      >
                        <LabelIcon sx={{ fontSize: "1.2rem" }} />
                        {label.count > 0 && (
                          <Box
                            sx={{
                              position: "absolute",
                              top: 2,
                              right: 1,
                              backgroundColor: "#dd4b39",
                              color: "white",
                              borderRadius: "20%",
                              minWidth: "16px",
                              height: "16px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontSize: "0.65rem",
                              fontWeight: "bold",
                              boxShadow: "0 2px 4px rgba(0,0,0,0.15)",
                              border: "1px solid rgba(255,255,255,0.2)",
                            }}
                          >
                            {label.count}
                          </Box>
                        )}
                      </IconButton>
                    </Tooltip>
                  );
                })}

                {trainingInProgress && (
                  <Tooltip title="Processing your inbox, Labels will be displayed automatically once training is complete." placement="right">
                    <Box sx={{
                      width: "100%",
                      height: "40px",
                      mb: 0.5,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      '@keyframes hourglassFlipShrunk': {
                        '0%': { transform: 'rotate(0deg)' },
                        '50%': { transform: 'rotate(180deg)' },
                        '100%': { transform: 'rotate(360deg)' },
                      },
                      '@keyframes hourglassPulseShrunk': {
                        '0%, 100%': { opacity: 1 },
                        '50%': { opacity: 0.6 },
                      },
                      animation: 'hourglassFlipShrunk 2s ease-in-out infinite, hourglassPulseShrunk 1.2s ease-in-out infinite',
                    }}>
                      <HourglassTopIcon sx={{ fontSize: 24 }} color="action" />
                    </Box>
                  </Tooltip>
                )}
              </>
            </List>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default EmailClassificationPanel;
